/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { DynamicScriptLoaderService } from './dynamic-script-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { LiveChatStateService } from '../../modules/live-chat/services/live-chat-state.service';
import { UserService } from './user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Attendance } from '../../modules/live-chat/interfaces/attendance';
import { ZoidApp } from '../interfaces/zoid-app';
import { ZoidOpenApp } from '../interfaces/zoid-open-app';
import { ZoidWindowScript } from '../interfaces/zoid-window-script';
import { ZoidAppsService } from './zoid-apps.service';
import { ZoidAppModalService } from './zoid-app-modal.service';
import { ZoidAppContext } from '../interfaces/zoid-app-context';
import { ZoidAppHandler } from '../interfaces/zoid-app-handler';
import { AppEventTypes } from '../interfaces/zoid-app-event-types';
import { LogEvent } from '../enums/log-event';
import { LoggerService } from './logger.service';
import { BehaviorSubject, catchError, combineLatest, firstValueFrom, map, of, pairwise, Subject, take, timeout, TimeoutError } from 'rxjs';
import { AttendanceService } from '../../modules/live-chat/services/attendance.service';
import { ThemeService, ToastService } from 'asksuite-citrus';
import { AppBarService } from '../../modules/live-chat/services/app-bar.service';
import { CompanyService } from './company.service';
import { StartService } from './start.service';
import { FilterService } from 'src/app/modules/live-chat/services/filter.service';
import { FilterState } from 'src/app/modules/live-chat/interfaces/attendance-list-filter.interface';
import { AttendantService } from '../../modules/live-chat/services/attendant.service';
import {
  AttendantsWithGroups,
  AttendantsWithGroupsBff,
  TransferAttendant,
  TransferAttendantBff,
} from '../../modules/live-chat/interfaces/manual-transfer.interface';
import { sortAlphabetical } from '../../modules/live-chat/helpers/sort-alphabetical.helper';
import { WhatsappTemplateService } from '../../shared/services/whatsapp-template/whatsapp-template.service';
import { WhatsappStartAttendanceService } from '../../shared/services/whatsapp-start-attendance/whatsapp-start-attendance.service';
import {
  GetContactByPhoneRequest,
  StartWhatsappAttendanceRequest,
} from '../../shared/services/whatsapp-start-attendance/start-whatsapp-attendance-request';
import { BroadcastMediatorService } from './broadcast-mediator.service';
import { AppResult, SendDataToAppArgs } from '../interfaces/zoid-sdk';
import { WINDOW_INJECTION_TOKEN } from '@core/constants/injection-tokens';
import { CallStatus } from '@core/enums/call-status.enum';
import { SharedApplicationStateService } from 'src/app/shared/services/shared-application-state/shared-application-state.service';
import { ResponsivenessService } from './responsiveness.service';
import { FeatureToggleService } from './feature-toggle.service';
import { CreditsManagementService } from 'src/app/modules/live-chat/services/credits-management.service';
import { CreditsBalanceService } from './credits-balance.service';
import { ICalculateCreditsConsumptionByExternalId, ICalculateCreditsConsumptionResponse, TotalCredits } from 'src/app/modules/live-chat/interfaces/credits-management.interface';
import { OtherAppMessage } from './zoid-app.types';
import { FeatureToggles } from '@core/enums/feature-toggles.enum';
import {AudioPlayerService} from "../../modules/live-chat/services/audio-player.service";
import {RingtoneVolumeService} from "../../modules/live-chat/services/ringtone-volume.service";
import { IframeHealthCheckService } from './iframe-health-check.service';
import { KanbanOverlayService } from '../../modules/live-chat/services/kanban-overlay.service';


@Injectable({ providedIn: 'root' })
export class ZoidAppService {
  public currentApps: ZoidApp[] = [];
  public redirectRouteApp?: string;
  public currentEmailData?: any;
  public currentPhoneData?: any;
  public currentWhatsappData?: any;
  public currentThemeData?: any;
  public connectionId?: string;
  public filterState?: FilterState;
  private loadedApps: ZoidApp[] = [];
  private pendingPhoneRestore?: { status: any; callStatus: any };
  private handlerQueue: { [key: string]: string[] } = {};
  private handlers: ZoidAppHandler[] = [];
  private insideOpenedApp: { [key: string]: ZoidApp | undefined } = {};
  private _kanbanSplitViewExitedForApp = false;
  
  //Services
  private destroyRef = inject(DestroyRef);
  private userService = inject(UserService);
  private companyService = inject(CompanyService);
  private startService = inject(StartService);
  private translateService = inject(TranslateService);
  private liveChatStateService = inject(LiveChatStateService);
  private dynamicScriptLoader = inject(DynamicScriptLoaderService);
  private appsService = inject(ZoidAppsService);
  private zoidAppModalService = inject(ZoidAppModalService);
  private attendanceService = inject(AttendanceService);
  private attendantService = inject(AttendantService);
  private logger = inject(LoggerService);
  private toastService = inject(ToastService);
  private appBarService = inject(AppBarService);
  private filterService = inject(FilterService);
  private whatsappTemplateService = inject(WhatsappTemplateService);
  private zone = inject(NgZone);
  private whatsappStartAttendanceService = inject(
    WhatsappStartAttendanceService
  );
  private windowInstance = inject(WINDOW_INJECTION_TOKEN);
  private themeService = inject(ThemeService);
  private sharedAppState = inject(SharedApplicationStateService);
  private responsivenessService = inject(ResponsivenessService);
  private featureToggleService = inject(FeatureToggleService);
  private creditsManagementService = inject(CreditsManagementService);
  private creditsBalanceService = inject(CreditsBalanceService);
  private audioPlayerService = inject(AudioPlayerService);
  private ringtoneVolumeService = inject(RingtoneVolumeService);
  private iframeHealthCheckService = inject(IframeHealthCheckService);
  private kanbanOverlayService = inject(KanbanOverlayService);


  // private chatComponent = inject(ChatComponent);
  private _otherAppMessage$ = new Subject<OtherAppMessage>();
  private openedFromAnotherApp$ = new BehaviorSubject(false);
  public readonly isForcedOpen$ = this.openedFromAnotherApp$.asObservable();
  private openedMobileApp$ = new BehaviorSubject(false);

  public readonly otherAppMessage$ = this._otherAppMessage$.asObservable();

  public get isBarVisible$() {
    return combineLatest([
      this.liveChatStateService.listenCurrentAttendance$,
      this.openedFromAnotherApp$,
    ]).pipe(
      map(([attendance, openedFromAnotherApp]) => {
        if (openedFromAnotherApp) return true;
        return Boolean(attendance);
      }),
    );
  }

  public get isBarVisibleToMobile$() {
    return this.openedMobileApp$;
  }

  constructor() {
    this.setupSubscriptions();
    this.attachFullScreenApps();
    this.setupHealthCheckSubscription();
  }

  public openAppWithData = async (appName: string, action: string, data: any) => {
    const isAlreadyOpen = this.currentApps.some(a => a.name === appName);
    return this.sendDataToAnotherApp({
      toApp: appName,
      fromApp: 'livechat',
      action,
      data,
      displayApp: !isAlreadyOpen,
    });
  };

  public openApp = async (openData: ZoidOpenApp, preload?: boolean) => {
    // Company data (getSelectedCompanies/getUserCompanies below) is only
    // reliably populated once the session bootstrap finishes — wait for it
    // before exposing the app context, otherwise an app opened very early
    // could see an empty company list. sessionSettled$ resolves on success
    // or failure so a broken session never blocks this forever; the local
    // timeout additionally bounds the wait well under the 30s HTTP default
    // so a stalled request doesn't make opening an app feel frozen. 10s is
    // a starting guess, not a measured worst case — logged so it can be
    // tuned against real data instead of guessed again later.
    await firstValueFrom(
      this.startService.sessionSettled$.pipe(
        timeout(10000),
        catchError((error) => {
          if (error instanceof TimeoutError) {
            this.logger.log(LogEvent.LIVECHAT_OPEN_APP_SESSION_TIMEOUT, {
              appName: openData.appName,
            });
          }
          return of(undefined);
        })
      )
    );

    const app = this.appsService.getApp(openData.appName);
    if (this.shouldAutoCloseApp(app) || this.isAppCurrentlyOpen(app)) {
      this.hideApp(app);
      return;
    }
    if (this.shouldCloseCurrentApps(app)) {
      this.currentApps
        .filter(currentApp => currentApp.name !== 'phoneApp')
        .forEach(currentApp => this.hideApp(currentApp));
    }
    if (!this.canOpenApp(app)) {
      return;
    }
    if (!this.loadedApps.includes(app)) {
      this.loadedApps.push(app);
      const appItem = await this.dynamicScriptLoader.loadScript(app);
      const appScript = (this.windowInstance as unknown as ZoidWindowScript)[
        appItem.scriptName
      ];

      if (!appScript) {
        this.logger.error(
          LogEvent.LIVECHAT_OPEN_APP_ERROR,
          new Error(
            `Trying to open an App that is not set: ${appItem.scriptName}`
          )
        );
        return;
      }

      const appZoidInstance = appScript({
        openData,
        getCurrentContext: this.getCurrentContext,
        getRedirectRouteApp: this.getRedirectRouteApp,

        hideApp: (result?: AppResult) => this.hideApp.bind(this)(app, result),
        hideOtherApp: (appName: string) => this.hideOtherApp(appName),
        unloadApp: () => this.unloadApp(app),
        isShown: () => this.isShown(app),

        onShowApp: (handler: any) =>
          this.addHandlerOnShowFunction(app, handler),
        onHideApp: (handler: any) =>
          this.addHandlerOnHideFunction(app, handler),
        onLoadApp: (handler: any) =>
          this.addHandlerOnLoadFunction(app, handler),
        onUnloadApp: (handler: any) =>
          this.addHandlerOnUnloadFunction(app, handler),
        sendDataToApp: (args: SendDataToAppArgs) =>
          this.sendDataToAnotherApp({ ...args, fromApp: app.name }),
        listenDataFromApp: (handler: any) =>
          this.addHandlerOnListenDataFromApp(app, handler),
        onRedirectRouteApp: (handler: any) =>
          this.addHandlerOnRedirectRouteApp(app, handler),
        onChangeAttendance: (handler: any) =>
          this.addHandlerOnChangeAttendance(app, handler),
        onSelectedAttendanceUpdate: (handler: any) =>
          this.addHandlerOnSelectedAttendanceUpdate(app, handler),
        onWhatsappChatAttendanceChange: (handler: any) =>
          this.handlerWhatsappChatChange(app, handler),
        onEmailChatAttendanceChange: (handler: any) =>
          this.handlerEmailChatChange(app, handler),
        onPhoneAppAttendanceChange: (handler: any) =>
          this.handlerPhoneChatChange(app, handler),
        trackEvent: (handler: any) =>
          this.trackEvent(handler),
        // TODO: remove @Deprecated
        context: this.getCurrentContext(),
        user: this.getCurrentContext().user,
        getCurrentUser: () => this.getCurrentContext().user,
        toggleBar: () => this.hideApp(app),
        getLocale: () => this.getCurrentContext().locale,
        getSelectedCompanies: () => {
          return this.companyService.getCompanies() || [];
        },
        companyCanAccess: (companiesIds: string[], featureName: string) => {
          return firstValueFrom(this.featureToggleService.companyCanAccess(companiesIds, featureName));
        },
        calculateConsumption: (data: ICalculateCreditsConsumptionByExternalId) => this.calculateConsumption(data),
        getBalanceReport: (companyIds: string[]) =>
          firstValueFrom(this.creditsBalanceService.fetchReport(companyIds)),
        getUserCompanies: () => this.getCurrentContext().selectedCompanies,
        onChangeConversation: (handler: any) =>
          this.addHandlerOnChangeAttendance(app, handler),
        onChangeTheme: (handler: any) =>
          this.addHandlerOnChangeTheme(app, handler),
        canQuoteUpdate: (handler: any) =>this.addHandlerCanQuoteUpdate(app, handler),
        
        onCloseApp: () => this.hideApp(app),
        onToastError: (message: string) => this.onToastError(message),
        onToastSuccess: (message: string) => this.onToastSuccess(message),
        listContactAttendancesHistory:
          this.onListContactAttendanceHistory.bind(this),
        getAttendanceDetails: this.getAttendanceDetails.bind(this),
        getCompanyCredits: this.getCompanyCredits.bind(this),
        getCompanyAttendantsWithGroups:
          this.getCompanyAttendantsWithGroups.bind(this),
        getWhatsappTemplates: this.handleWhatsappTemplate.bind(this),
        startWhatsappAttendance: this.startWhatsappAttendance.bind(this),
        getContactByPhone: this.getContactByPhone.bind(this),
        getCompaniesIdsToSearchContact:
          this.getCompaniesIdsToSearchContact.bind(this),
        phoneActions: this.phoneActions.bind(this),
      });

      appZoidInstance.render(`#${app.name}`);

      setTimeout(() => {
        this.attachAbsoluteChild(app.name);
      }, 200);

      this.dispatchHandlers(app, AppEventTypes.load, true);
    }

    if (!preload) {
      this.dispatchHandlers(app, AppEventTypes.show, true);
      this.currentApps?.push(app);
    }
  };

  private isAppCurrentlyOpen(app: ZoidApp): boolean {
    return this.currentApps.some(currentApp => currentApp.name === app.name);
  }
  
  private shouldCloseCurrentApps(app: ZoidApp): boolean {
    const isEmailOrWhatsapp = ['emailAttendanceApp', 'whatsappStartConversation'].includes(app.name);
    if (isEmailOrWhatsapp && this.currentApps.length > 0) {
      return true;
    }
    if (app.name === 'phoneApp') {
      return false;
    }
    const hasNonFullScreenApp = this.currentApps.some(currentApp => 
      !currentApp.fullScreen && 
      currentApp.name !== app.name
    );
    return hasNonFullScreenApp;
  }
  
  private canOpenApp(app: ZoidApp): boolean {
    const hasPhoneApp = this.currentApps.some(a => a.name === 'phoneApp');
    const isMidasApp = app.name === 'asksuiteMidas';
    const isPhoneAppOpen = app.name === 'phoneApp';
    const isEmailOpen = this.currentApps.some(a => a.name === 'emailAttendanceApp');
  
    if (isEmailOpen && (isMidasApp || isPhoneAppOpen)) return true;
    if (isEmailOpen && app.name !== 'asksuiteMidas') return false;
    if (this.currentApps.length === 1 && !hasPhoneApp && !isPhoneAppOpen) return false;
    if (this.currentApps.length >= 2 && !(isEmailOpen && isMidasApp)) return false;
    return true;
  }
  

  private attachAbsoluteChild(appName: string) {
    const windowWidth = window.innerWidth;
    if (windowWidth <= 767 && appName === 'whatsappStartConversation') {
      const parentContainer = document.querySelector(`#${appName}`);
      if (parentContainer) {
        const childElement = parentContainer.firstElementChild;
        if (childElement) {
          childElement.classList.add('absolute-child');
        }
      }
    }
  }

  private handlerWhatsappChatChange(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.inviteForConversation, handler);
    this.addHandler(app, AppEventTypes.startWhatsappConversation, handler);

    const wasQueued = !!(
      this.handlerQueue[app.id]?.includes(AppEventTypes.inviteForConversation) ||
      this.handlerQueue[app.id]?.includes(AppEventTypes.startWhatsappConversation)
    );

    this.handlerQueueConsume(app, AppEventTypes.inviteForConversation);
    this.handlerQueueConsume(app, AppEventTypes.startWhatsappConversation);

    if (!wasQueued && this.currentWhatsappData) {
      const event =
        this.currentWhatsappData.type === 'start'
          ? AppEventTypes.startWhatsappConversation
          : AppEventTypes.inviteForConversation;
      this.dispatchHandlers(app, event);
    }

    this.currentWhatsappData = undefined;
  }

  private handlerEmailChatChange(app: ZoidApp, handler: any) {
    const alreadyHadHandler =
      this.getHandlers(app, AppEventTypes.updateEmailData).length > 0;

    this.addHandler(app, AppEventTypes.updateEmailData, handler);

    const wasQueued = !!this.handlerQueue[app.id]?.includes(
      AppEventTypes.updateEmailData
    );

    this.handlerQueueConsume(app, AppEventTypes.updateEmailData);

    if (!wasQueued && !alreadyHadHandler && this.currentEmailData) {
      this.dispatchHandlers(app, AppEventTypes.updateEmailData);
    }

    this.currentEmailData = undefined;
  }

  private handlerPhoneChatChange(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.updatePhoneData, handler);
    this.handlerQueueConsume(app, AppEventTypes.updatePhoneData);
  }

  private handleWhatsappTemplate(companyId: string) {
    return firstValueFrom(this.whatsappTemplateService.getTemplates(companyId));
  }

  private startWhatsappAttendance(request: StartWhatsappAttendanceRequest) {
    return firstValueFrom(
      this.whatsappStartAttendanceService.startWhatsappAttendance(request)
    );
  }

  private shouldAutoCloseApp(app: ZoidApp): boolean {
    return this.currentApps.some(currentApp => currentApp.id === app?.id) && app?.loadOptions?.autoClose;
  }

  private allowHotJarOnApp(appName: string) {
    document
      .querySelector(`#${appName} > div > iframe`)
      ?.setAttribute('data-hj-allow-iframe', '');
  }

  private removeScrollOnApp(appName: string){
      document
        .querySelector(`#${appName} > div > iframe:nth-of-type(2)`)
        ?.setAttribute('scrolling', 'no');
  }

  private subscribeToWhatsappStartChat() {
    BroadcastMediatorService.listen('whatsappChat')?.subscribe((data) => {
      let event: AppEventTypes;

      this.currentWhatsappData = data;

      if (data?.type === 'start') event = AppEventTypes.startWhatsappConversation;
      else event = AppEventTypes.inviteForConversation;
      this.loadedApps.forEach((app) => this.dispatchHandlers(app, event, true, data));
    });
  }

  private subscribeToEmailChat() {
    BroadcastMediatorService.listen('emailApp')?.subscribe((data) => {
      this.currentEmailData = data;
      this.loadedApps.forEach((app) =>
        this.dispatchHandlers(app, AppEventTypes.updateEmailData, true)
      );
    });
  }

  private subscribeToPhone() {
    BroadcastMediatorService.listen('phoneApp')?.subscribe((data) => {
      this.currentPhoneData = data;
      this.loadedApps.forEach((app) =>
        this.dispatchHandlers(app, AppEventTypes.updatePhoneData, true)
      );
    });
  }

  private subscribeToTheme() {
    BroadcastMediatorService.listen('theme')?.subscribe((data) => {
      this.currentThemeData = data;
      this.loadedApps.forEach((app) =>
        this.dispatchHandlers(app, AppEventTypes.changeTheme, true)
      );
    });
  }

  private subscribeToSelectedAttendanceUpdates = () => {
    this.liveChatStateService.listenCurrentAttendance$
      .pipe(takeUntilDestroyed(this.destroyRef), pairwise())
      .subscribe(([prevAttendance, currAttendance]) => {
        this.loadedApps.map((app) => {
          if (prevAttendance?.id !== currAttendance?.id) {
            this.dispatchHandlers(app, AppEventTypes.changeAttendance, true);
          }

          this.dispatchHandlers(
            app,
            AppEventTypes.selectedAttendanceUpdate,
            true,
            currAttendance
          );
        });
      });
  };

  private subscribeFilterState = () => {
    this.filterState = this.filterService.filterState;
    this.filterService.filterState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.filterState = this.filterService.filterState;
      });
  };

  private subscribeConnection = () => {
    this.connectionId = this.liveChatStateService.currentSocketConnectionId;
    this.liveChatStateService.socketConnectionId$.subscribe((connectionId) => {
      this.connectionId = connectionId;
    });
  };

  private subscribeToCanQuoteUpdate(){
    this.sharedAppState.canQuote$().subscribe((canQuote) => {
      this.loadedApps.forEach((app) => {
        this.dispatchHandlers(app, AppEventTypes.canQuoteUpdate, true, canQuote);
      });
    });
  }

  private setupSubscriptions() {
    this.subscribeToSelectedAttendanceUpdates();
    this.subscribeFilterState();
    this.subscribeConnection();
    this.subscribeToWhatsappStartChat();
    this.subscribeToEmailChat();
    this.subscribeToPhone();
    this.subscribeToTheme();
    this.subscribeToCanQuoteUpdate();
  }

  public preloadApps = (zoidAppReplace?: Partial<ZoidApp>) => {
    this.appsService.apps
      .map((app) => {
        if (!zoidAppReplace || zoidAppReplace.name !== app.name) return app;
        return { ...app, ...zoidAppReplace };
      })
      .filter((app) => app.loadOptions && app.loadOptions.preload)
      .map(async (app) => {
        return this.openApp({ appName: app.name }, true);
      });
  };

  private attachFullScreenApps() {
    const fullScreenApps = this.appsService.apps.filter((app) => app.fullScreen);
    fullScreenApps.forEach(app => {
      this.zoidAppModalService.attach(app.name);
    });
  }

  private getCurrentContext = (): ZoidAppContext => {
    const user = {
      ...this.userService.getCurrentUser(),
    };

    const selectedAttendance = this.liveChatStateService.currentAttendance;

    const context = {
      user,
      newLivechat: true,
      connectionId: this.connectionId,
      selectedAttendance: selectedAttendance && {
        id: selectedAttendance.id,
        publicId: selectedAttendance.publicId,
        companyId: selectedAttendance.currentCompany.id,
        conversationId:
          selectedAttendance.currentContactChannel?.channelReferenceId,
        contactChannelReferenceId: selectedAttendance.currentContactChannel?.contactChannelReferenceId,
        currentAttendant: selectedAttendance.currentAttendant,
        isBotStopped: selectedAttendance.isBotStopped,
        status: selectedAttendance.status,
        lead: selectedAttendance.contact && {
          name: selectedAttendance.contact?.name,
          email: selectedAttendance.contact?.email,
          phone: selectedAttendance.contact?.phone,
        },
        locale: selectedAttendance.currentLanguage,
        lastMessageAt: selectedAttendance.lastMessageAt,
        channel: selectedAttendance.currentContactChannel?.channel,
      },
      selectedCompanies: this.companyService.getCompanies() || [],
      filterCompanies: this.filterState?.companyIds || [],
      locale: this.translateService.currentLang,
    };

    return {
      ...(context as ZoidAppContext),
      // TODO: remove @Deprecated
      publicAttendanceId: selectedAttendance?.publicId,
      selectedLead: context.selectedAttendance?.lead,
      selectedCompanyId: selectedAttendance?.currentCompany.id,
      selectedConversationId:
        selectedAttendance?.currentContactChannel?.channelReferenceId,
      currentAppData:  this.currentApps[this.currentApps.length - 1],
      phoneAppData: {
        adapter: 'code7',
        agentEmail: user.customFields?.code7AgentEmail,
        agentId: user.customFields?.code7AgentId,
        agentPassword: user.customFields?.code7AgentPassword,
        phoneExtension: user.customFields?.code7PhoneExtension,
      },
    };
  };

  private runCallbacks(callbacks: ((data?: any) => void)[], data?: any) {
    return callbacks.map((callback) => {
      return callback(data);
    });
  }

  public isShown(app: ZoidApp) {
    return this.currentApps.some(currentApp => currentApp.id === app.id);
  }

  public hideAllApps(): void {
    [...this.currentApps].forEach((app) => this.hideApp(app));
  }

  private loadApp = (app: ZoidApp, callbacks: (() => void)[] = []) => {
    this.allowHotJarOnApp(app.name);
    this.removeScrollOnApp(app.name);
    
    // Start health check when phoneApp is loaded
    if (app.name === 'phoneApp') {
      this.startHealthCheck();
    }
    
    return this.runCallbacks(callbacks);
  };

  private unloadApp = (app: ZoidApp, callbacks: (() => void)[] = []) => {
    this.dynamicScriptLoader.unloadScript(app.id);
    this.currentApps = this.currentApps.filter(currentApp => currentApp.id !== app.id);
    return this.runCallbacks(callbacks);
  };

  private showApp = (app: ZoidApp, callbacks: (() => void)[] = []) => {
    this.sendThemeOnShowApps()
    const isMobile = window.innerWidth <= 767;
    if (app.fullScreen) {
      this.zoidAppModalService.show(app.name);
    } else {
      this.appBarService.setCurrentApp(app);
      this.liveChatStateService.setAppBarCollapsed(false);
      if ((isMobile && app.name === 'phoneApp') || (this.currentApps.length === 1 && this.currentApps[0]?.name === "emailAttendanceApp")) {
        this.openedMobileApp$.next(true);
      }
    }
    return this.runCallbacks(callbacks);
  };

  sendThemeOnShowApps(){
    const theme = this.themeService.getTheme();
    BroadcastMediatorService.notify('theme', theme)
  }

  private hideOtherApp(appName: string) {
    const app = this.appsService.getApp(appName);
    this.hideApp(app);
  }

  private hideApp(app: ZoidApp, result?: AppResult) {
    const isMobile = window.innerWidth <= 767;
    this.currentApps = this.currentApps.filter(currentApp => currentApp.id !== app.id);
    if (app.fullScreen) {
      this.zoidAppModalService.hide(app.name);
      this.closeInsideOpenedApps(app);
      if (this.currentApps.length === 0) {
        this.openedFromAnotherApp$.next(false);
        this.openedMobileApp$.next(false);
      }
    } else {
      this.appBarService.hide(app.name);
      this.removeFromInsideOpenedApps(app);
      if (this.currentApps.length === 0) {
        this.openedMobileApp$.next(false);
        this.openedFromAnotherApp$.next(false);
        this._kanbanSplitViewExitedForApp = false;
        if (isMobile) {
          this.liveChatStateService.setAppBarCollapsed(true);
        }
      } else if( isMobile && this.currentApps.length === 1 && this.currentApps[0].name === "emailAttendanceApp") {
        this.openedMobileApp$.next(false);
      }
    }

    if (result) this.handleAppResult(result);
    this.runCallbacksByAppEvent(app, AppEventTypes.hide);
  }

  private closeInsideOpenedApps(app: ZoidApp) {
    const insideOpenedApp = this.insideOpenedApp[app.name];
    const shouldRestoreSplitView = this._kanbanSplitViewExitedForApp;

    if (insideOpenedApp) {
      this.hideApp(insideOpenedApp);
      this.insideOpenedApp[app.name] = undefined;
    }

    this._kanbanSplitViewExitedForApp = false;

    if (shouldRestoreSplitView && this.kanbanOverlayService.isOpen && !this.hasAppOpenedFromFullScreenApp()) {
      this.kanbanOverlayService.splitView();
    }
  }

  private removeFromInsideOpenedApps(app: ZoidApp) {
    for (const parentAppName in this.insideOpenedApp) {
      if (this.insideOpenedApp[parentAppName]?.name === app.name) {
        this.insideOpenedApp[parentAppName] = undefined;
        break;
      }
    }
  }

  public hasAppOpenedFromFullScreenApp(): boolean {
    return Object.values(this.insideOpenedApp).some(app => app !== undefined);
  }

  private handleAppResult(result: AppResult) {
    if (result.withSuccessToast)
      this.toastService.success(result.withSuccessToast);
  }

  private onChangeAttendance(callbacks: (() => void)[] = []) {
    return this.runCallbacks(callbacks);
  }

  private onChangeTheme(callbacks: (() => void)[] = []) {
    return this.runCallbacks(callbacks, this.currentThemeData);
  }

  private onSelectedAttendanceUpdate(callbacks: (() => void)[] = [], data = null) {
    return this.runCallbacks(callbacks,data);
  }

  private onRedirectRouteApp(callbacks: (() => void)[] = []) {
    return this.runCallbacks(callbacks);
  }

  private addHandler = (
    app: ZoidApp,
    event: AppEventTypes,
    callback: () => void
  ) => {
    this.handlers.push({
      app,
      event,
      callback,
    });
  };

  private getHandlers = (
    app: ZoidApp,
    event: AppEventTypes
  ): ZoidAppHandler[] => {
    return this.handlers.filter(
      (handler) => handler.app.id === app.id && handler.event === event
    );
  };

  private dispatchHandlers(
    app: ZoidApp,
    event: AppEventTypes,
    addToQueue?: boolean,
    data: any = null
  ) {
    const handlers = this.getHandlers(app, event);
    const callbacks = handlers.map((handler) => handler.callback);

    switch (event) {
      case AppEventTypes.load:
        this.loadApp(app, callbacks);
        break;
      case AppEventTypes.unload:
        this.unloadApp(app, callbacks);
        break;
      case AppEventTypes.show:
        this.showApp(app, callbacks);
        break;
      case AppEventTypes.changeAttendance:
        this.onChangeAttendance(callbacks);
        break;
      case AppEventTypes.changeTheme:
        this.onChangeTheme(callbacks);
        break;
      case AppEventTypes.selectedAttendanceUpdate:
        this.onSelectedAttendanceUpdate(callbacks,data);
        break;
      case AppEventTypes.redirectRouteApp:
        this.onRedirectRouteApp(callbacks);
        break;
      case AppEventTypes.inviteForConversation:
        this.onInviteForConversation(callbacks,data);
        break;
      case AppEventTypes.startWhatsappConversation:
        this.onStartWhatsappConversation(callbacks);
        break;
      case AppEventTypes.updateEmailData:
        this.onUpdateEmailData(callbacks);
        break;
      case AppEventTypes.updatePhoneData:
        this.onUpdatePhoneData(callbacks);
        break;
      case AppEventTypes.canQuoteUpdate:
        this.onUpdateCanQuote(callbacks,data);
        break;
    }

    if (!handlers.length && addToQueue) {
      this.handlerQueue[app.id] = [...(this.handlerQueue[app.id] || []), event];
    }
  }

  private onStartWhatsappConversation(callbacks: ((data?: any) => void)[]) {
    return this.runCallbacks(callbacks, {
      ...this.getCurrentContext(),
      data: this.currentWhatsappData,
    });
  }

  private onUpdateEmailData(callbacks: ((data?: any) => void)[]) {
    return this.runCallbacks(callbacks, this.currentEmailData);
  }

  private onUpdatePhoneData(callbacks: ((data?: any) => void)[]) {
    return this.runCallbacks(callbacks, this.currentPhoneData);
  }

  private onUpdateCanQuote(callbacks: ((data?: any) => void)[],data = null) {
    return this.runCallbacks(callbacks, data);
  }

  private onInviteForConversation(callbacks: ((data?: any) => void)[],data = null) {
    return this.runCallbacks(callbacks, {
      ...this.getCurrentContext(),
      data: data ?? this.currentWhatsappData,
    });
  }

  private handlerQueueConsume(app: ZoidApp, event: AppEventTypes) {
    if (this.handlerQueue[app.id]?.includes(event)) {
      this.dispatchHandlers(app, event);
      this.handlerQueue[app.id] = this.handlerQueue[app.id].filter(
        (qe) => qe !== event
      );
    }
  }

  private addHandlerOnShowFunction(app: ZoidApp, handler: () => void) {
    this.addHandler(app, AppEventTypes.show, handler);
    this.handlerQueueConsume(app, AppEventTypes.show);
  }

  private addHandlerOnHideFunction(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.hide, handler);
    this.handlerQueueConsume(app, AppEventTypes.hide);
  }

  private addHandlerOnLoadFunction(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.load, handler);
    this.handlerQueueConsume(app, AppEventTypes.load);
  }

  private addHandlerOnUnloadFunction(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.unload, handler);
    this.handlerQueueConsume(app, AppEventTypes.unload);
  }

  private addHandlerOnRedirectRouteApp(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.redirectRouteApp, handler);
    this.handlerQueueConsume(app, AppEventTypes.redirectRouteApp);
  }

  private addHandlerOnChangeAttendance(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.changeAttendance, handler);
    this.handlerQueueConsume(app, AppEventTypes.changeAttendance);
  }

  private addHandlerOnChangeTheme(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.changeTheme, handler);
    this.handlerQueueConsume(app, AppEventTypes.changeTheme);
  }

  private addHandlerOnSelectedAttendanceUpdate(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.selectedAttendanceUpdate, handler);
    this.handlerQueueConsume(app, AppEventTypes.selectedAttendanceUpdate);
    this.dispatchHandlers(app, AppEventTypes.selectedAttendanceUpdate, undefined, this.liveChatStateService.currentAttendance);
  }

  private addHandlerCanQuoteUpdate(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.canQuoteUpdate, handler);
    this.handlerQueueConsume(app, AppEventTypes.canQuoteUpdate);
    this.sharedAppState.canQuote$().pipe(take(1)).subscribe((canQuote)=>{
      this.dispatchHandlers(app, AppEventTypes.canQuoteUpdate, undefined, canQuote);
    })
  }

  private addHandlerOnListenDataFromApp(app: ZoidApp, handler: any) {
    this.addHandler(app, AppEventTypes.messageToAnotherApp, handler);
    this.handlerQueueConsume(app, AppEventTypes.messageToAnotherApp);
  }

  private async sendDataToAnotherApp(args: SendDataToAppArgs) {
    const { fromApp, toApp, displayApp, action, data, redirectRouteApp } = args;
    
    if (toApp && toApp == 'livechat') {

      this._otherAppMessage$.next({action: action as OtherAppMessage['action'], data, fromApp: fromApp!});

      if (
        ['openInHistory', 'openInAttendance'].includes(action) &&
        this.responsivenessService.isScreenLessThan(970)
      ) {
        this.hideOtherApp(fromApp!);
      }

      return;
    }

    const toAppInstance = this.appsService.getApp(toApp);

    if (displayApp) {
      if (redirectRouteApp) {
        this.redirectRouteApp = redirectRouteApp;
      }
      await this.openApp({
        appName: toApp,
        openConfiguration: { openerApp: fromApp, ...data },
      });
      if (this.kanbanOverlayService.isSplitView) {
        this.kanbanOverlayService.exitSplitView();
        this._kanbanSplitViewExitedForApp = true;
      }
      this.openedFromAnotherApp$.next(true);
      this.insideOpenedApp[fromApp!] = toAppInstance;
    }

    const handler = this.getHandlers(
      toAppInstance,
      AppEventTypes.messageToAnotherApp
    );
    if (!handler) return;

    handler.forEach((h) => h.callback({ fromApp, action, data }));
  }

  private runCallbacksByAppEvent(app: ZoidApp, event: AppEventTypes) {
    const handlers = this.getHandlers(app, event);
    const callbacks = handlers.map((handler) => handler.callback);
    return this.runCallbacks(callbacks);
  }

  private onListContactAttendanceHistory() {
    const selectedAttendance = this.liveChatStateService.currentAttendance
    if (!selectedAttendance) return Promise.resolve(undefined);
    return firstValueFrom(
      this.attendanceService.listContactAttendancesByAttendanceId(
        selectedAttendance.id,
        selectedAttendance.currentCompany.id
      )
    );
  }

  private getAttendanceDetails(attendanceId: string, companyId: string) {
    if (!this.liveChatStateService.currentAttendance) return Promise.resolve(undefined);
    return firstValueFrom(
      this.attendanceService.getAttendanceDetails(attendanceId, companyId)
    );
  }

  private async getCompanyCredits(companyId: string): Promise<TotalCredits | undefined> {
    if (!companyId) return Promise.resolve(undefined);

    return firstValueFrom(this.attendanceService.getCompanyCredits(companyId));
  }

  private async calculateConsumption(
    data: ICalculateCreditsConsumptionByExternalId
  ): Promise<ICalculateCreditsConsumptionResponse> {
    return firstValueFrom(
      this.creditsManagementService.calculateConsumption(data)
    );
  }

  private getCompanyAttendantsWithGroups(
    companyId: string
  ): Promise<AttendantsWithGroups | undefined> {
    if (!companyId) return Promise.resolve(undefined);

    return firstValueFrom(
      this.attendantService
        .getCompanyAttendantsWithGroups(companyId)
        .pipe(map(this.formatCompanyAttendantsValue.bind(this)))
    );
  }

  private onToastSuccess(message: string) {
    this.toastService.success(message);
  }

  private onToastError(message: string) {
    this.toastService.danger(message);
  }

  private getRedirectRouteApp = (): string | undefined => {
    const redirectRouteApp = this.redirectRouteApp;

    this.redirectRouteApp = undefined;

    return redirectRouteApp;
  };

  private formatCompanyAttendantsValue(data: AttendantsWithGroupsBff) {
    const attendants = this.getAttendantsDetailsFormatted(
      data?.attendants || []
    );

    return {
      attendants,
      groups: data.groups,
    };
  }

  private getAttendantsDetailsFormatted(
    attendants: TransferAttendantBff[]
  ): TransferAttendant[] {
    const orderedAttendants = this.orderAttendants(attendants);

    return orderedAttendants.map((attendant) => {
      const status = attendant.isOnline ? 'online' : 'busy';
      const statusTooltip = attendant.isOnline
        ? 'STATUS.ONLINE'
        : 'STATUS.OFFLINE';
      const groups = attendant.groups.map((group) => group.name).join(', ');

      return {
        ...attendant,
        status,
        statusTooltip,
        groups,
      };
    });
  }

  private orderAttendants(attendants: TransferAttendantBff[]) {
    const online: TransferAttendantBff[] = [];
    const offline: TransferAttendantBff[] = [];

    attendants
      .sort((a, b) => sortAlphabetical(a.name, b.name))
      .forEach((attendant) =>
        (attendant.isOnline ? online : offline).push(attendant)
      );

    return [...online, ...offline];
  }

  private async getContactByPhone(request: GetContactByPhoneRequest) {
    return firstValueFrom(
      this.whatsappStartAttendanceService.getContactByPhone(request)
    );
  }

  private getCompaniesIdsToSearchContact(): string[] {
    return this.userService.isCurrentUserGlobal()
      ? this.filterService.filterState?.companyIds || []
      : this.companyService
        .getCompanies()
        ?.map((company) => company.companyId) || [];
  }

  private async phoneActions(request: any) {
    const ringtoneAudio = 'ringtone.mp3';
    switch (request.type) {
      case 'PLAY_RINGTONE':
        void this.audioPlayerService
          .play(ringtoneAudio, { loop: true, volume: this.ringtoneVolumeService.volume })
          .catch(() => {});
        break;
      case 'STOP_RINGTONE':
        await this.audioPlayerService.stop(ringtoneAudio);
        break;
      case 'WEBPHONE_SHOW_VOLUME_ICON':
        this.ringtoneVolumeService.setIconVisible(true);
        break;
      case 'WEBPHONE_HIDE_VOLUME_ICON':
        this.ringtoneVolumeService.setIconVisible(false);
        break;
      case 'CHANGE_STATUS':
        this.liveChatStateService.setPhoneStatus(request.status);
        
        try {
          if (this.pendingPhoneRestore) {
            this.logger.log(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_RECOVERED, {
              message: 'Restoring phoneApp status after recovery',
            });
            const phoneApp = this.loadedApps.find(app => app.name === 'phoneApp');
            if (phoneApp) {
              this.currentPhoneData = {
                ...this.currentPhoneData,
                restoreStatus: this.pendingPhoneRestore.status,
                restoreCallStatus: this.pendingPhoneRestore.callStatus
              };
              
              this.pendingPhoneRestore = undefined;
              
              this.dispatchHandlers(phoneApp, AppEventTypes.updatePhoneData);
              this.logger.log(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_RECOVERED, {
                message: 'PhoneApp status restored after recovery',
              });
            }
          }
          
        } catch (error) {
          console.error('Failed to restore phoneApp status', error);
        }
        break;
      case 'CHANGE_CALL_STATUS':
        this.liveChatStateService.setPhoneCallStatus(request.callStatus);
        if (
          [
            CallStatus.RECEIVING_CALL,
            CallStatus.RECEIVING_TRANSFER,
            CallStatus.ON_CALL,
          ].includes(request.callStatus)
        ) {
          const app = this.appsService.getApp('phoneApp');
          if(this.isAppCurrentlyOpen(app)){
            return;
          }
          this.setBarVisible(true);
          requestAnimationFrame(() => {
            this.openApp({ appName: 'phoneApp' }).then();
          });
        } else if (
          [
            CallStatus.DISCONNECTED,
            CallStatus.CLOSED,
            CallStatus.REFUSED,
          ].includes(request.callStatus)
        ) {
          setTimeout(() => {
            if (
              this.liveChatStateService.currentPhoneCallStatus ===
              request.callStatus
            ) {
              this.liveChatStateService.resetPhoneCallStatus();
            }
          }, 500);
        }
        break;
    }
  }

  public setBarVisible(visible: boolean) {
    this.openedFromAnotherApp$.next(visible);
  }

  public setBarVisibleToMobile(visible: boolean) {
    this.openedMobileApp$.next(visible);
  }

  private trackEvent(handler: any) {
    this.logger.log(handler.eventName, handler.payload)
  }

  /**
   * Setup subscription to health check events
   */
  private setupHealthCheckSubscription() {
    this.logger.info(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_SUBSCRIPTION, {
      message: 'Setting up reinstantiate phoneApp subscription',
    });
    // Subscribe to reinstantiate phoneApp when freezing is detected
    this.iframeHealthCheckService.reinstantiatePhoneApp$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.reinstantiatePhoneApp();
      });
  }

  /**
   * Reinstantiate phoneApp when freezing is detected
   * This method clears the phoneApp container, removes it from loaded apps,
   * and reopens it to restore functionality
   */
  private async reinstantiatePhoneApp() {
    this.logger.log(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_NOT_RECEIVED_PONG, {
      message: 'Starting phoneApp reinstantiation due to freezing',
    });

    const phoneApp = this.appsService.getApp('phoneApp');
    if (!phoneApp) {
      this.logger.error(LogEvent.LIVECHAT_OPEN_APP_ERROR, new Error('phoneApp not found'));
      return;
    }

    // Check if phoneApp was loaded and currently open
    const wasLoaded = this.loadedApps.some(app => app.name === 'phoneApp');
    const wasOpen = this.currentApps.some(app => app.name === 'phoneApp');

    if (!wasLoaded) {
      this.logger.warn(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_NOT_RECEIVED_PONG, {
        message: 'phoneApp was not loaded, skipping reinstantiation',
      });
      return;
    }

    // Save current phone status before reinstantiation
    const previousPhoneStatus = this.liveChatStateService.currentPhoneAppStatus;
    const previousCallStatus = this.liveChatStateService.currentPhoneCallStatus;

    try {
      // Step 1: Clear the phoneApp DOM container
      const phoneAppContainer = document.querySelector('#phoneApp');
      if (phoneAppContainer) {
        phoneAppContainer.innerHTML = '';
      }

      // Step 2: Remove the script from DOM and clean references
      this.dynamicScriptLoader.unloadScript(phoneApp.id);

      // Step 3: Remove phoneApp from loaded and current apps
      this.loadedApps = this.loadedApps.filter(app => app.name !== 'phoneApp');
      this.currentApps = this.currentApps.filter(app => app.name !== 'phoneApp');

      // Step 4: Clear handlers for phoneApp
      this.handlers = this.handlers.filter(handler => handler.app.name !== 'phoneApp');

      // Step 5: Wait a bit before reinstantiating to ensure cleanup is complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // Step 6: Reopen the phoneApp
      await this.openApp({ appName: 'phoneApp' }, !wasOpen);

      // Step 7: If the app was previously open, show it
      if (wasOpen) {
        this.showApp(phoneApp);
      }

      // Step 8: Restore previous phone status after phoneApp is loaded
      this.restorePhoneStatusAfterRecovery(previousPhoneStatus, previousCallStatus);

      this.logger.log(LogEvent.LIVECHAT_IFRAME_HEALTHCHECK_RECOVERED, {
        message: 'PhoneApp successfully reinstantiated. It will automatically reconnect and set status to ONLINE.',
      });

    } catch (error) {
      this.logger.error(LogEvent.LIVECHAT_OPEN_APP_ERROR, error as Error);
    }
  }

  /**
   * Restore phone status after phoneApp recovery
   * Stores the previous status to be restored when the phoneApp sends SOFTPHONE_TRY_CONNECTION event
   */
  private restorePhoneStatusAfterRecovery(previousStatus: any, previousCallStatus: any) {
    if (previousStatus) {
      this.pendingPhoneRestore = {
        status: previousStatus,
        callStatus: previousCallStatus
      };
    }
  }

  /**
   * Start health check for phoneApp
   */
  private startHealthCheck() {
    this.iframeHealthCheckService.start();
  }
}
